import { useEffect } from 'react';
import { useCustomLayer, useMapStore } from '@uofu-uss-gis/ui-react';
import { createParkingLotPopupTemplate } from '../utils/parkingLotPopupTemplate';
import { EVENT_PARKING_LAYER_CONFIG, buildSurfaceLotsUrl } from '../config/eventParkingConfig';

// Set of ArcGIS layer IDs for all event parking layers, used to identify event features in popup handlers
const EVENT_PARKING_MAP_LAYER_IDS = new Set(
  EVENT_PARKING_LAYER_CONFIG.flatMap((c) =>
    Array.isArray(c.mapLayerId) ? c.mapLayerId : [c.mapLayerId]
  )
);

const parkingLotPopupTemplate = createParkingLotPopupTemplate();
const UTAH_RED_HIGHLIGHT_NAMES = new Set(['default', 'temporary']);
const UTAH_RED_HIGHLIGHTS = [
  {
    name: 'default',
    color: '#BE0000',
    haloOpacity: 0.9,
    fillOpacity: 0.2,
  },
  {
    name: 'temporary',
    color: '#BE0000',
    haloOpacity: 0.9,
    fillOpacity: 0.2,
  },
] as const;

type MapView = NonNullable<ReturnType<typeof useMapStore.getState>['mapView']>;
type GoToTarget = Parameters<MapView['goTo']>[0];
type GoToOptions = Parameters<MapView['goTo']>[1];
type CoordinateLike = {
  latitude?: number | null;
  longitude?: number | null;
};
type GeometryLike = CoordinateLike & {
  centroid?: CoordinateLike;
};
type PopupActionEvent = {
  action?: {
    id?: string;
  };
};
type PopupFeature = {
  layer?: {
    id?: string;
  };
  geometry?: GeometryLike & {
    type?: string;
    extent?: {
      expand: (factor: number) => GoToTarget;
    };
  } & Record<string, unknown>;
  attributes?: {
    PLOT?: string;
    LOTNUMBER?: string;
  };
};

function isGoToInterruptedError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: string }).name === 'view:goto-interrupted'
  );
}

function goToFeature(mapView: MapView, target: unknown, options?: GoToOptions) {
  return mapView.goTo(target as GoToTarget, options).catch((error: unknown) => {
    if (isGoToInterruptedError(error)) {
      return undefined;
    }

    console.error(error);
    return undefined;
  });
}

// Coordinate overrides for lots where the polygon centroid does not navigate Google Maps to the correct entrance.
// Keyed by PLOT (or LOTNUMBER) attribute value; value is [lat, lng].
const LOT_COORDINATE_OVERRIDES: Record<string, [number, number]> = {
  '05':  [40.76135, -111.84761],
  '10':  [40.76021, -111.844],
  '12':  [40.76102, -111.84251],
  '24':  [40.76504, -111.842],
  '31':  [40.76763, -111.84469],
  '33':  [40.76579, -111.84775],
  '35':  [40.76453, -111.852],
  '46':  [40.77052, -111.843],
  '52':  [40.77209, -111.83790],
  '54':  [40.76901, -111.836],
  '62':  [40.77047, -111.834],
  '63':  [40.7667,  -111.836],
  '65':  [40.76996, -111.83311],
  '72':  [40.76822, -111.832],
  '73':  [40.76812, -111.831],
  '75':  [40.76622, -111.828],
  '78':  [40.76399, -111.83414],
  '83':  [40.76397, -111.834],
  '87':  [40.76245, -111.83],
  '94':  [40.7526,  -111.827],
  '101': [40.75806, -111.831],
  '118': [40.76089, -111.822],
};

/**
 * Opens Google Maps driving directions to a parking lot.
 * Destination is resolved in priority order:
 *   1. A known coordinate override for lots where the centroid navigates incorrectly
 *   2. The polygon centroid
 *   3. A direct lat/lng on the geometry (e.g. point features)
 *   4. The popup click location as a last resort
 * Origin is the user's current geolocation when available, otherwise omitted so
 * Google Maps prompts for it.
 */
function openGoogleMapsDirections(geometry: GeometryLike, plot?: string, fallbackLocation?: CoordinateLike | null) {
  const centroid = geometry?.centroid;
  let destinationLat = centroid?.latitude ?? geometry?.latitude ?? fallbackLocation?.latitude;
  let destinationLng = centroid?.longitude ?? geometry?.longitude ?? fallbackLocation?.longitude;

  // Apply per-lot override if one exists for this plot number
  const override = plot != null ? LOT_COORDINATE_OVERRIDES[plot] : undefined;
  if (override) {
    [destinationLat, destinationLng] = override;
  }

  if (destinationLat == null || destinationLng == null) {
    return;
  }

  const destination = `${destinationLat},${destinationLng}`;
  const baseUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;

  if (!navigator.geolocation) {
    window.open(baseUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const origin = `${pos.coords.latitude},${pos.coords.longitude}`;
      const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    // If geolocation is denied or times out, open Maps without an origin
    () => {
      window.open(baseUrl, '_blank', 'noopener,noreferrer');
    },
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
  );
}

/**
 * Controller component that adds parking lot layer to the map
 * with clickable popups showing parking information
 */
export function ParkingLotController() {
  const mapView = useMapStore((state) => state.mapView);

  useEffect(() => {
    if (!mapView) {
      return;
    }

    const existingHighlights = mapView.highlights
      .toArray()
      .filter((highlight) => !UTAH_RED_HIGHLIGHT_NAMES.has(highlight.name));

    mapView.highlights = [...UTAH_RED_HIGHLIGHTS, ...existingHighlights];
  }, [mapView]);

  useCustomLayer({
    layerId: 'parkingLots',
    mapLayerId: 'parkingLotsLayer',
    url: buildSurfaceLotsUrl(),
    outFields: ['*'],
    visible: true,
    opacity: 0, // Make layer invisible but still interactive
    popupTemplate: parkingLotPopupTemplate,
    definitionExpression: 'interactive=1',
  });

  // Listen for the Directions popup action and open Google Maps for the selected lot
  useEffect(() => {
    const popup = mapView?.popup;
    if (!popup) {
      return;
    }

    const handle = popup.on('trigger-action', (event: PopupActionEvent) => {
      if (event.action?.id !== 'directions-action') {
        return;
      }
      const feature = popup.selectedFeature as PopupFeature | null | undefined;
      const geometry = feature?.geometry;
      if (geometry) {
        // PLOT is the primary lot identifier; fall back to LOTNUMBER for event parking lots
        const plot = (feature?.attributes?.PLOT ?? feature?.attributes?.LOTNUMBER) as string | undefined;
        openGoogleMapsDirections(geometry, plot, popup.location);
      }
    });

    return () => handle?.remove();
  }, [mapView]);

  // When a popup opens on an event parking feature, zoom the map to the lot extent
  useEffect(() => {
    const popup = mapView?.popup;
    if (!popup) {
      return;
    }

    const handle = popup.watch('selectedFeature', (feature: PopupFeature | null | undefined) => {
      const layerId = feature?.layer?.id as string | undefined;
      if (!layerId || !EVENT_PARKING_MAP_LAYER_IDS.has(layerId)) {
        return;
      }
      const geometry = feature?.geometry;
      if (!geometry) return;

      if (geometry.type === 'point') {
        // Zoom to the point at a useful scale rather than trying to expand a null extent
        void goToFeature(mapView, { target: geometry, zoom: 18 }, { animate: true });
      } else if (geometry.extent) {
        void goToFeature(mapView, geometry.extent.expand(4), { animate: true });
      }
    });

    return () => handle?.remove();
  }, [mapView]);

  return null;
}

